import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { DocumentChartBarIcon } from '@heroicons/react/24/outline'
import { AuthContextApi } from '../../Context/api'
import { isRelatorioEmAndamento } from '../../utils/relatorioStatus'
import Header from '../../Components/Header'
import RelatoriosTable from '../../Components/Table/Relatorios'
import ModalGerarRelatorio from '../../Components/Modal/Relatorio/Gerar'

const PER_PAGE = 10

const Relatorios = () => {
    const {
        getRelatorios,
        getRelatorioObras,
        postRelatorio,
        downloadRelatorioObra,
        getDataExibicao,
        fetchEmpreendimentos,
        fetchObras,
    } = useContext(AuthContextApi)

    const [relatorios, setRelatorios] = useState([])
    const [loading, setLoading] = useState(false)
    const [page, setPage] = useState(0)

    const [obrasCache, setObrasCache] = useState({})
    const [obrasLoading, setObrasLoading] = useState({})

    const [modalOpen, setModalOpen] = useState(false)
    const [datasExibicao, setDatasExibicao] = useState([])
    const [loadingDatas, setLoadingDatas] = useState(false)
    const [empreendimentos, setEmpreendimentos] = useState([])
    const [loadingEmpreendimentos, setLoadingEmpreendimentos] = useState(false)
    const [obras, setObras] = useState([])
    const [loadingObras, setLoadingObras] = useState(false)

    const loadRelatorios = useCallback(async (targetPage = page, { silent = false } = {}) => {
        if (!silent) setLoading(true)
        const data = await getRelatorios({
            page: targetPage,
            per_page: PER_PAGE,
            sort: 'data_criacao',
            sort_dir: 'desc',
        })
        setRelatorios(Array.isArray(data) ? data : [])
        if (!silent) setLoading(false)
    }, [getRelatorios, page])

    const temProcessamentoAtivo = relatorios.some((item) => isRelatorioEmAndamento(item.status))
    const pollingRef = useRef(null)

    useEffect(() => {
        loadRelatorios(page)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page])

    useEffect(() => {
        if (pollingRef.current) clearInterval(pollingRef.current)
        if (!temProcessamentoAtivo) return undefined

        pollingRef.current = setInterval(() => {
            loadRelatorios(page, { silent: true })
        }, 15000)

        return () => {
            if (pollingRef.current) clearInterval(pollingRef.current)
        }
    }, [temProcessamentoAtivo, page, loadRelatorios])

    useEffect(() => () => {
        if (pollingRef.current) clearInterval(pollingRef.current)
    }, [])

    const loadObras = async (processamentoId) => {
        setObrasLoading((prev) => ({ ...prev, [processamentoId]: true }))
        const data = await getRelatorioObras(processamentoId, {
            page: 0,
            per_page: 100,
            sort: 'data_criacao',
            sort_dir: 'asc',
        })
        setObrasCache((prev) => ({ ...prev, [processamentoId]: Array.isArray(data) ? data : [] }))
        setObrasLoading((prev) => ({ ...prev, [processamentoId]: false }))
    }

    const openModal = async () => {
        setModalOpen(true)
        setEmpreendimentos([])
        setObras([])
        setLoadingDatas(true)
        const datas = await getDataExibicao({ page: 0, per_page: 100, sort: 'data_inicial', sort_dir: 'desc' })
        setDatasExibicao(Array.isArray(datas) ? datas : [])
        setLoadingDatas(false)
    }

    const searchEmpreendimentos = async (term) => {
        setLoadingEmpreendimentos(true)
        const params = new URLSearchParams({
            page: 0,
            per_page: 20,
            sort: 'nome_empreendimento',
            sort_dir: 'asc',
        })
        if (term?.trim()) {
            params.set('filter', 'nome_empreendimento')
            params.set('search', term.trim())
        }
        const data = await fetchEmpreendimentos(params.toString())
        setEmpreendimentos(data)
        setLoadingEmpreendimentos(false)
    }

    const selectEmpreendimento = (empreendimento) => {
        setObras([])
    }

    const searchObras = async (empreendimentoId, term) => {
        if (!empreendimentoId) return
        setLoadingObras(true)
        const params = new URLSearchParams({
            empreendimento_id: empreendimentoId,
            page: 0,
            per_page: 20,
            sort: 'nome_obra',
            sort_dir: 'asc',
            filter: 'nome_obra',
            search: term.trim(),
        })
        const data = await fetchObras(params.toString())
        setObras(data)
        setLoadingObras(false)
    }

    const handleGenerate = async (payload) => {
        const ok = await postRelatorio(payload)
        if (ok) {
            setModalOpen(false)
            setPage(0)
            setObrasCache({})
            await loadRelatorios(0)
        }
    }

    return (
        <>
            <Header
                title="Relatórios"
                description="Acompanhe o processamento e baixe relatórios por obra"
                icon={DocumentChartBarIcon}
                buttonText="Gerar relatório"
                buttonAction={openModal}
                hasAction
                hasReturn={false}
                color="yellow"
            />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <RelatoriosTable
                    data={relatorios}
                    loading={loading}
                    page={page}
                    recordsPerPage={PER_PAGE}
                    previous={() => setPage((p) => Math.max(0, p - 1))}
                    next={() => setPage((p) => p + 1)}
                    onLoadObras={loadObras}
                    onDownload={downloadRelatorioObra}
                    obrasCache={obrasCache}
                    obrasLoading={obrasLoading}
                />
            </div>

            <ModalGerarRelatorio
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                onSave={handleGenerate}
                datasExibicao={datasExibicao}
                loadingDatas={loadingDatas}
                onSearchEmpreendimentos={searchEmpreendimentos}
                onSelectEmpreendimento={selectEmpreendimento}
                onSearchObras={searchObras}
                empreendimentos={empreendimentos}
                loadingEmpreendimentos={loadingEmpreendimentos}
                obras={obras}
                loadingObras={loadingObras}
            />
        </>
    )
}

export default Relatorios
